import { useState } from "react";

import type { SldElement, SldElementCategory, SldElements } from "../services/sldElements";
import {
  BUS_KIND_LABELS,
  DEVICE_KIND_LABELS,
  FEEDER_KIND_LABELS,
  LOAD_KIND_LABELS,
  SOURCE_KIND_LABELS,
} from "../sld/sldDraft";

// One control for a code a study names - a bus, a device, a motor (SLD-b1, A27 (b), (e)).
// With the elements of the revision's diagram it is a select over the codes of the wanted
// categories ("code · kind · voltage"), "Choose…" first and an explicit last choice "Other
// code (not in the diagram)" that turns the control into a text input - the typed code is
// kept and the backend will say REVIEW about it. Without elements (no diagram in the
// revision, no project, still loading) it is the plain text input the forms had before,
// under the same label, so nothing changes for a revision without a diagram. A code already
// in the draft that the diagram does not know stays visible in the text input; it is never
// dropped.

export const CHOOSE_CODE = "Choose…";
export const OTHER_CODE = "__other__";
export const OTHER_CODE_LABEL = "Other code (not in the diagram)";
export const PICK_FROM_DIAGRAM = "Pick from the diagram";

const KIND_LABELS: Partial<Record<SldElementCategory, Record<string, string>>> = {
  bus: BUS_KIND_LABELS,
  source: SOURCE_KIND_LABELS,
  feeder: FEEDER_KIND_LABELS,
  device: DEVICE_KIND_LABELS,
  load: LOAD_KIND_LABELS,
};

/** "MSB-01 · PCC · 415 V": the code, its kind's words when known, its voltage when known. */
export function sldElementOptionText(element: SldElement): string {
  const parts = [element.code];
  if (element.kind !== null) {
    parts.push(KIND_LABELS[element.category]?.[element.kind] ?? element.kind);
  }
  if (element.voltage_v !== null) {
    parts.push(`${element.voltage_v} V`);
  }
  return parts.join(" · ");
}

/**
 * The elements of the wanted categories - and, when given, of the wanted kinds (e.g. the
 * MOTOR loads) - in the order the API lists them (by code).
 */
export function sldElementsOf(
  elements: SldElements | null | undefined,
  categories: readonly SldElementCategory[],
  kinds?: readonly string[],
): SldElement[] {
  if (!elements || elements.version < 1) {
    return [];
  }
  return elements.elements.filter(
    (element) =>
      categories.includes(element.category) &&
      (kinds === undefined || (element.kind !== null && kinds.includes(element.kind))),
  );
}

type SldElementPickerProps = {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  /** The categories a code may name here, e.g. ["bus"] or ["device"]. */
  categories: readonly SldElementCategory[];
  /** The kinds within them, e.g. ["MOTOR"] for a starter's motor; every kind when left out. */
  kinds?: readonly string[];
  elements?: SldElements | null | undefined;
  disabled?: boolean;
  inputMode?: "text" | "decimal";
};

export function SldElementPicker({
  label,
  name,
  value,
  onChange,
  categories,
  kinds,
  elements,
  disabled = false,
  inputMode = "text",
}: SldElementPickerProps) {
  const candidates = sldElementsOf(elements, categories, kinds);
  const codes = candidates.map((element) => element.code);
  // "Other code" chosen: the control stays a text input until the engineer asks for the list.
  const [typing, setTyping] = useState(false);
  const known = value === "" || codes.includes(value);
  const selectMode = candidates.length > 0 && !typing && known;

  if (selectMode) {
    return (
      <label>
        {label}
        <select
          name={name}
          value={value}
          disabled={disabled}
          data-sld-picker="select"
          onChange={(event) => {
            if (event.target.value === OTHER_CODE) {
              setTyping(true);
              return;
            }
            onChange(event.target.value);
          }}
        >
          <option value="">{CHOOSE_CODE}</option>
          {candidates.map((element) => (
            <option key={element.code} value={element.code}>
              {sldElementOptionText(element)}
            </option>
          ))}
          <option value={OTHER_CODE}>{OTHER_CODE_LABEL}</option>
        </select>
      </label>
    );
  }

  return (
    <label>
      {label}
      <input
        name={name}
        value={value}
        disabled={disabled}
        inputMode={inputMode}
        data-sld-picker={candidates.length > 0 ? "text" : "plain"}
        onChange={(event) => onChange(event.target.value)}
      />
      {candidates.length > 0 ? (
        <button
          type="button"
          disabled={disabled}
          data-sld-picker-back="true"
          onClick={() => {
            // Back to the list: a code the diagram does not know is let go on purpose here.
            if (!known) {
              onChange("");
            }
            setTyping(false);
          }}
        >
          {PICK_FROM_DIAGRAM}
        </button>
      ) : null}
    </label>
  );
}

export default SldElementPicker;
